import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  Api as ApiIcon,
  AttachMoney as AttachMoneyIcon,
  Speed as SpeedIcon,
  Today as TodayIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useZincApiLogs, useZincApiLogSummary } from "../../../hooks/useZincApiLogs";
import { ZincApiLogFilter } from "../../../types/zincApiLog";
import PeriodSelector from "../../../components/backend/analytics/PeriodSelector";
import KpiStatCard from "../../../components/backend/analytics/KpiStatCard";
import { AnalyticsFilter } from "../../../types/analytics";

const endpointTypeColors: Record<string, string> = {
  search: "#6366f1",
  details: "#10b981",
  offers: "#f59e0b",
  realtime_search: "#ec4899",
  realtime_details: "#8b5cf6",
  realtime_offers: "#06b6d4",
};

const statusCodeColor = (code: number | null): string => {
  if (!code) return "#9ca3af";
  if (code >= 200 && code < 300) return "#10b981";
  if (code >= 400 && code < 500) return "#f59e0b";
  return "#ef4444";
};

const ZincApiUsagePage: React.FC = () => {
  const { t } = useTranslation();
  const [periodFilter, setPeriodFilter] = useState<AnalyticsFilter>({
    period: "30d",
  });
  const [page, setPage] = useState(1);
  const [endpointType, setEndpointType] = useState<string>("");

  const filter: ZincApiLogFilter = {
    pageNumber: page,
    pageSize: 20,
    period: periodFilter.period,
    startDate: periodFilter.startDate,
    endDate: periodFilter.endDate,
    endpointType: endpointType || undefined,
  };

  const { data: logsResponse, isLoading: logsLoading } = useZincApiLogs(filter);
  const { data: summaryResponse, isLoading: summaryLoading } =
    useZincApiLogSummary(filter);

  const logs = logsResponse?.data;
  const summary = summaryResponse?.data;

  if (summaryLoading && !summary) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight={700}>
            {t("admin.analytics.zincApiUsage")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t("admin.analytics.zincApiUsageDesc")}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>{t("admin.analytics.endpointType")}</InputLabel>
            <Select
              value={endpointType}
              label={t("admin.analytics.endpointType")}
              onChange={(e) => {
                setEndpointType(e.target.value);
                setPage(1);
              }}
            >
              <MenuItem value="">{t("common.all")}</MenuItem>
              <MenuItem value="search">{t("admin.analytics.endpointSearch")}</MenuItem>
              <MenuItem value="details">{t("admin.analytics.endpointDetails")}</MenuItem>
              <MenuItem value="offers">{t("admin.analytics.endpointOffers")}</MenuItem>
              <MenuItem value="realtime_search">{t("admin.analytics.endpointRealtimeSearch")}</MenuItem>
              <MenuItem value="realtime_details">{t("admin.analytics.endpointRealtimeDetails")}</MenuItem>
              <MenuItem value="realtime_offers">{t("admin.analytics.endpointRealtimeOffers")}</MenuItem>
            </Select>
          </FormControl>
          <PeriodSelector
            filter={periodFilter}
            onChange={(f) => {
              setPeriodFilter(f);
              setPage(1);
            }}
          />
        </Box>
      </Box>

      {/* KPI Cards */}
      {summary && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md>
            <KpiStatCard
              title={t("admin.analytics.totalApiCalls")}
              data={{
                currentValue: summary.totalCalls,
                previousValue: 0,
                changePercentage: 0,
                changeDirection: "flat",
              }}
              icon={<ApiIcon />}
              color="#6366f1"
            />
          </Grid>
          <Grid item xs={12} sm={6} md>
            <KpiStatCard
              title={t("admin.analytics.estimatedCost")}
              data={{
                currentValue: summary.estimatedCost,
                previousValue: 0,
                changePercentage: 0,
                changeDirection: "flat",
              }}
              prefix="$"
              icon={<AttachMoneyIcon />}
              color="#10b981"
            />
          </Grid>
          <Grid item xs={12} sm={6} md>
            <KpiStatCard
              title={t("admin.analytics.avgResponseTime")}
              data={{
                currentValue: summary.avgDurationMs,
                previousValue: 0,
                changePercentage: 0,
                changeDirection: "flat",
              }}
              suffix="ms"
              icon={<SpeedIcon />}
              color="#f59e0b"
            />
          </Grid>
          <Grid item xs={12} sm={6} md>
            <KpiStatCard
              title={t("admin.analytics.callsToday")}
              data={{
                currentValue: summary.callsToday,
                previousValue: 0,
                changePercentage: 0,
                changeDirection: "flat",
              }}
              icon={<TodayIcon />}
              color="#ec4899"
            />
          </Grid>
          <Grid item xs={12} sm={6} md>
            <KpiStatCard
              title={t("admin.analytics.successRate")}
              data={{
                currentValue:
                  summary.totalCalls > 0
                    ? Math.round(
                        (summary.successCount / summary.totalCalls) * 100
                      )
                    : 0,
                previousValue: 0,
                changePercentage: 0,
                changeDirection: "flat",
              }}
              suffix="%"
              icon={<CheckCircleIcon />}
              color="#10b981"
            />
          </Grid>
        </Grid>
      )}

      {/* Endpoint Type Breakdown */}
      {summary && Object.keys(summary.callsByEndpointType).length > 0 && (
        <Card sx={{ mb: 4 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              {t("admin.analytics.callsByEndpoint")}
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
              {Object.entries(summary.callsByEndpointType).map(
                ([type, count]) => (
                  <Chip
                    key={type}
                    label={`${type}: ${count}`}
                    sx={{
                      fontWeight: 600,
                      fontSize: "13px",
                      height: 32,
                      backgroundColor: `${
                        endpointTypeColors[type] || "#6b7280"
                      }15`,
                      color: endpointTypeColors[type] || "#6b7280",
                      border: `1px solid ${
                        endpointTypeColors[type] || "#6b7280"
                      }30`,
                    }}
                  />
                )
              )}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Logs Table */}
      <Card>
        <CardContent sx={{ p: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6" fontWeight={600}>
              {t("admin.analytics.apiCallLog")}
            </Typography>
            {logs && (
              <Typography variant="body2" color="text.secondary">
                {logs.totalCount} {t("admin.analytics.totalRecords")}
              </Typography>
            )}
          </Box>

          {logsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow
                      sx={{
                        background:
                          "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
                      }}
                    >
                      <TableCell sx={{ fontWeight: 700 }}>
                        {t("admin.analytics.dateTime")}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        {t("admin.analytics.endpointType")}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        {t("admin.analytics.method")}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        {t("admin.analytics.statusCode")}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        {t("admin.analytics.duration")}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        {t("admin.analytics.user")}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        {t("admin.analytics.ipAddress")}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {logs?.items.map((log) => (
                      <TableRow
                        key={log.id}
                        sx={{
                          "&:hover": { background: "#f8fafc" },
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {new Date(log.createdAt).toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {log.zincEndpointType && (
                            <Chip
                              label={log.zincEndpointType}
                              size="small"
                              sx={{
                                fontWeight: 600,
                                fontSize: "11px",
                                height: 22,
                                backgroundColor: `${
                                  endpointTypeColors[log.zincEndpointType] ||
                                  "#6b7280"
                                }18`,
                                color:
                                  endpointTypeColors[log.zincEndpointType] ||
                                  "#6b7280",
                              }}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={log.httpMethod}
                            size="small"
                            sx={{
                              fontWeight: 700,
                              fontSize: "10px",
                              height: 20,
                              backgroundColor:
                                log.httpMethod === "GET"
                                  ? "#dbeafe"
                                  : "#fef3c7",
                              color:
                                log.httpMethod === "GET"
                                  ? "#2563eb"
                                  : "#d97706",
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{
                              color: statusCodeColor(log.responseStatusCode),
                            }}
                          >
                            {log.responseStatusCode ?? "—"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {log.durationMs}ms
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              maxWidth: 150,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {log.userEmail || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {log.ipAddress || "—"}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                    {logs?.items.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <ErrorIcon
                              sx={{ fontSize: 40, color: "#d1d5db" }}
                            />
                            <Typography color="text.secondary">
                              {t("admin.analytics.noDataAvailable")}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              {logs && logs.totalPages > 1 && (
                <Box
                  sx={{ display: "flex", justifyContent: "center", mt: 3 }}
                >
                  <Pagination
                    count={logs.totalPages}
                    page={logs.pageNumber}
                    onChange={(_, value) => setPage(value)}
                    color="primary"
                    shape="rounded"
                  />
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default ZincApiUsagePage;
